import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';

import { getEmptySelections } from '../../../../../components/FilterModal/constants';
import { applyFilterFromOutside } from '../../../../../components/FilterModal/handlers/applyFilterFromOutside';
import { assetsFiltersAtom } from '../../../../../store/product/filters';
import { useGlobalAssetContext } from '../contexts';

import { minValueSearchSchema, SearchFormData, validateFormSearchSchema } from './constants';

export const useGlobalAssetController = () => {
  const setAssetsFiltersAtom = useSetRecoilState(assetsFiltersAtom);
  const {
    setProductsTabs,
    selectedTab,
    setSearchTerm,
    setCurrentPage,
    availableFilters,
    orderBy,
    setOrderBy,
    searchTerm: searchTermContext,
  } = useGlobalAssetContext();

  const formSearch = useForm<SearchFormData>({
    resolver: zodResolver(validateFormSearchSchema(minValueSearchSchema[selectedTab.id])),
    defaultValues: { searchTerm: '' },
    mode: 'all',
  });

  const handleUpdateSearchTerm = ({ searchTerm }: SearchFormData) => {
    setCurrentPage(1);
    setSearchTerm(searchTerm);
  };

  const handleClearSearchTerm = () => {
    setCurrentPage(1);
    setSearchTerm('');
    formSearch.setValue('searchTerm', '');
  };

  const handleClearOrderBy = () => {
    setOrderBy(undefined);
  };

  const handleChangeTab = (index: number) => {
    handleClearSearchTerm();
    handleClearOrderBy();
    setProductsTabs((prevTabs) => prevTabs.map((tab, i) => ({ ...tab, selected: i === index })));
  };

  const applyCategoriesFilter = (fieldName: string, selections: string[]) => {
    setCurrentPage(1);
    setAssetsFiltersAtom((prevFilters) =>
      applyFilterFromOutside(prevFilters, fieldName, selections)
    );
  };

  const handleAutoOrderBy = (value?: string) => {
    const isSameCurrent = orderBy?.value === value;

    const getDirection = () => {
      if (!isSameCurrent) {
        return 'asc';
      }
      return orderBy?.direction === 'asc' ? 'desc' : 'asc';
    };

    handleOrderBy(value, getDirection());
  };

  const handleOrderBy = (value?: string, forceDirection?: 'asc' | 'desc') => {
    setCurrentPage(1);
    if (!value) {
      setOrderBy(undefined);
      return;
    }
    setOrderBy({
      value,
      direction: forceDirection || 'asc',
    });
  };

  useEffect(() => {
    setAssetsFiltersAtom({
      appliedFilters: [],
      selectedFilters: getEmptySelections(availableFilters),
      availableFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFilters]);

  useEffect(() => {
    if (!searchTermContext) {
      formSearch.resetField('searchTerm');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTermContext]);

  const isValidSearch = formSearch.formState.isValid;

  return {
    formSearch,
    isValidSearch,
    applyCategoriesFilter,
    handleUpdateSearchTerm,
    handleClearSearchTerm,
    handleChangeTab,
    handleOrderBy,
    handleAutoOrderBy,
  };
};

export type GlobalAssetController = ReturnType<typeof useGlobalAssetController>;
